import { useUpdateEffect } from 'ahooks';
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  FormProps,
  Input,
  Popover,
  Result,
  Space,
  Switch,
  Tag,
  Typography,
  theme,
} from 'antd';
import { FC, useState } from 'react';
import { ArrowUpRightSquare, BoxArrowUpRight, ExclamationTriangle, FiletypeCss } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { AppDatePicker, AwesomeNodePath, ButtonSuccess, CodeEditor, CopyableText } from '@/common/components';
import { GoogleIndexingIcon } from '@/common/components/AppIcons';
import { AppFormProps, AppRoutes } from '@/common/interfaces';
import { CommonContainerTitle } from '@/common/layouts';
import { defineInputStatus, parseDate } from '@/common/utils';

import {
  Page,
  PageFormFields,
  PageFormInputs,
  PageLayoutsAutocomplete,
  PageNode,
  PagePublishedAtTag,
  PageUsageForBots,
  PageUsageForKeys,
  PageUsageForTag,
  PagesCriticalCssGenerationStatus,
  PagesCriticalCssStatusTag,
  PagesHelpNotes,
} from '@/modules/Pages';

import { PagesVariantsList } from './PagesVariantsList';

interface Props extends AppFormProps<typeof PageFormInputs, PageFormFields> {
  siteId: string;
  pageId: string;
  nodeId: string;
  pages: Pick<Page['structureNode']['pages'][0], 'id' | 'title' | 'usageFor'>[];
  pageCriticalCssStatus: PagesCriticalCssGenerationStatus;
  previewLink: string | null;
  structureNodeInfo: Pick<PageNode, 'id' | 'path'>;
  updatedAt: Date | null;
  siteHaveSearchConsole: boolean;
  handleKeyDown: FormProps['onKeyDown'];
  handleOpenPushIndexingModal: (() => void) | undefined;
  handleOpenCriticalCssModal: (() => void) | undefined;
  handleGetLayoutMutation?: (id: string) => Promise<void>;
}

export const PagesFormView: FC<Props> = ({
  formName,
  formType,
  formErrors,
  form,
  siteId,
  pageId,
  nodeId,
  updatedAt,
  initialValues,
  pages,
  pageCriticalCssStatus,
  previewLink,
  structureNodeInfo,
  isFormLoading,
  validationSchema,
  siteHaveSearchConsole,
  handleBack,
  handleGetLayoutMutation,
  handleFinish,
  handleFinishFailed,
  handleKeyDown,
  handleOpenPushIndexingModal,
  handleOpenCriticalCssModal,
}) => {
  const { token } = theme.useToken();
  const [pagesListOpen, setPagesListOpen] = useState(false);
  const [isPagePublished, setIsPagePublished] = useState(false);

  const formPublishDate = Form.useWatch<Date>(PageFormInputs.PublishedAt, form);

  const formUsageFor = Form.useWatch<boolean>(PageFormInputs.UsageFor, form);
  const formBody = Form.useWatch<Record<string, string>>(PageFormInputs.Body, form);
  const formLayoutId = Form.useWatch<string | undefined>(PageFormInputs.LayoutId, form);

  const blocksNames = Object.keys(Object.fromEntries(Object.entries(formBody ?? {}).filter((el) => el[1] !== undefined)));

  useUpdateEffect(() => {
    setIsPagePublished(parseDate(new Date(formPublishDate)).isBefore(new Date()));
  }, [formPublishDate]);

  const handleClear = () => {
    form.setFieldValue(PageFormInputs.LayoutId, undefined);
    form.setFieldValue(PageFormInputs.Body, { content: '' });
  };

  return (
    <Card>
      <CommonContainerTitle
        extra={
          <>
            <Button target="_blank" href={previewLink || ''} icon={<BoxArrowUpRight />} disabled={isFormLoading || !previewLink}>
              Попереднiй перегляд
            </Button>
            <Divider type="vertical" style={{ height: 32 }} />

            <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button type="default" onClick={handleBack} disabled={isFormLoading}>
                Назад
              </Button>
              <ButtonSuccess
                loading={isFormLoading}
                type="primary"
                htmlType="submit"
                form={formType === 'edit' ? 'page-edit' : 'page-create'}
              >
                {formType === 'edit' ? 'Зберегти' : 'Створити'}
              </ButtonSuccess>
              <Divider type="vertical" style={{ height: 32 }} />
              <PagesHelpNotes />
            </Space>
          </>
        }
      >
        {formType === 'edit' && (
          <Space>
            <Popover
              trigger={['click']}
              open={pagesListOpen}
              onOpenChange={setPagesListOpen}
              placement="bottomLeft"
              content={<PagesVariantsList pageId={pageId} pages={pages} siteId={siteId} nodeId={nodeId} />}
            >
              <Button type="dashed" shape="round">
                Варіанти:&nbsp;<Tag color="blue-inverse">{pages?.length ?? 0}</Tag>
              </Button>
            </Popover>
            <Button
              type="dashed"
              shape="round"
              onClick={handleOpenPushIndexingModal}
              disabled={!siteHaveSearchConsole}
              icon={<GoogleIndexingIcon />}
            >
              Індексувати
            </Button>
            <Button
              type="dashed"
              shape="round"
              onClick={handleOpenCriticalCssModal}
              // disabled={pageCriticalCssStatus}
              icon={<FiletypeCss />}
            >
              Critical CSS
              <PagesCriticalCssStatusTag withText={false} status={pageCriticalCssStatus} />
            </Button>
          </Space>
        )}
      </CommonContainerTitle>

      <Form
        form={form}
        name={formName}
        labelCol={{ span: 5 }}
        labelAlign="left"
        wrapperCol={{ span: 10 }}
        initialValues={initialValues}
        autoComplete="off"
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        onKeyDown={handleKeyDown}
      >
        <Form.Item
          label="Заголовок"
          required
          name={PageFormInputs.Title}
          hasFeedback
          help={formErrors?.title}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.title)}
          rules={validationSchema.title}
        >
          <Input disabled={isFormLoading} allowClear placeholder="Введіть заголовок сторінки" />
        </Form.Item>

        <Form.Item
          required
          label="Структурна нода"
          name={PageFormInputs.StructureNodeId}
          hasFeedback
          help={formErrors?.parentNodeId}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.structureNodeId)}
          rules={validationSchema.structureNodeId}
        >
          <Card style={{ border: `1px solid ${token.colorBorder}` }} size="small">
            <Flex align="center" justify="space-between" gap={8}>
              <Link
                target="_blank"
                to={AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', structureNodeInfo.id)}
              >
                <Space>
                  <AwesomeNodePath style={{ fontSize: 16 }} path={structureNodeInfo.path} />
                  <ArrowUpRightSquare size={16} />
                </Space>
              </Link>
              <Space>
                <Button type="default" size="small">
                  <CopyableText textToCopy={structureNodeInfo.path} />
                </Button>
              </Space>
            </Flex>
          </Card>
        </Form.Item>

        <Form.Item
          label="Використовувати для"
          name={PageFormInputs.UsageFor}
          required
          hasFeedback
          hidden={formType === 'edit'}
          help={formErrors?.usageFor}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.usageFor)}
          rules={validationSchema.usageFor}
        >
          <Space>
            <Tag>За-замовчуванням</Tag>
            <Form.Item name={PageFormInputs.UsageFor} valuePropName="checked" noStyle>
              <Switch size="small" disabled={isFormLoading} loading={isFormLoading} />
            </Form.Item>
            <Tag color="geekblue">Bot: Google</Tag>
          </Space>
        </Form.Item>

        <Form.Item
          label="Використовувати для"
          required
          help={formErrors?.usageFor}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.usageFor)}
          rules={validationSchema.usageFor}
          hidden={formType !== 'edit'}
        >
          <PageUsageForTag value={formUsageFor ? { [PageUsageForKeys.Bot]: PageUsageForBots.Google } : { type: 'default' }} />
        </Form.Item>

        <Form.Item
          label="Макет"
          required
          name={PageFormInputs.LayoutId}
          hasFeedback
          help={formErrors?.layoutId}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.layoutId)}
          rules={validationSchema.layoutId}
          extra={
            <Flex align="center">
              <Typography.Text type="warning" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <ExclamationTriangle size={12} />
              </Typography.Text>
              &#8239;&nbsp;Новий макет замінить всі блоки цієї сторінки
            </Flex>
          }
        >
          <Flex gap={4}>
            <PageLayoutsAutocomplete
              siteId={siteId}
              disabled={isFormLoading}
              value={formLayoutId}
              onSelect={async (id) => {
                if (handleGetLayoutMutation) await handleGetLayoutMutation(id);
              }}
              onChange={(e) => form.setFieldValue(PageFormInputs.LayoutId, e)}
              onClear={handleClear}
            />
            <Button
              href={AppRoutes.PageLayoutsById.replace(':siteId', siteId).replace(':layoutId', formLayoutId || '')}
              target="_blank"
              type="link"
              icon={<BoxArrowUpRight />}
              disabled={isFormLoading || !formLayoutId}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label="Дата публікації"
          hasFeedback
          name={PageFormInputs.PublishedAt}
          help={formErrors?.publishedAt}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.publishedAt)}
          rules={validationSchema.publishedAt}
        >
          <Flex gap={10}>
            <Form.Item noStyle name={PageFormInputs.PublishedAt}>
              <AppDatePicker showTime disabled={isFormLoading} style={{ width: '100%' }} placeholder="Оберіть дату публікації" />
            </Form.Item>
            {formPublishDate &&
              (isPagePublished ? (
                <PagePublishedAtTag publishedAt={formPublishDate} isPublished={isPagePublished} />
              ) : (
                <PagePublishedAtTag publishedAt={formPublishDate} isPublished={isPagePublished} />
              ))}
          </Flex>
        </Form.Item>

        <Form.Item label="Дата оновлення">
          <Input
            readOnly
            style={{ width: '100%' }}
            disabled={!updatedAt || isFormLoading}
            value={updatedAt ? parseDate(updatedAt).format('DD.MM.YYYY hh:mm:ss') : 'Ніколи не оновлювалася'}
          />
        </Form.Item>

        <Divider />

        <Form.Item
          label="Вміст сторінки по блокам"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          required
          name={PageFormInputs.Body}
          style={{ marginBottom: 0 }}
        >
          {blocksNames.length ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {blocksNames.map((name) => (
                <Card
                  key={name}
                  size="small"
                  style={{ border: `1px solid ${token.colorBorder}` }}
                  title={
                    <Tag bordered={false} style={{ fontWeight: 'bold' }}>
                      {name}
                    </Tag>
                  }
                >
                  <Form.Item
                    key={name}
                    name={[PageFormInputs.Body, name]}
                    hasFeedback
                    validateStatus={defineInputStatus(isFormLoading)}
                    rules={validationSchema.body}
                    style={{ marginBottom: 0 }}
                  >
                    <CodeEditor disabled={isFormLoading} />
                  </Form.Item>
                </Card>
              ))}
            </Space>
          ) : (
            <Card>
              <Result status="warning" title="Макет не містить жодного блоку" />
            </Card>
          )}
          {formErrors?.body && (
            <Form.ErrorList
              errors={[
                <Typography.Text key={formErrors.body} type="danger">
                  {formErrors.body}
                </Typography.Text>,
              ]}
            />
          )}
        </Form.Item>
      </Form>
    </Card>
  );
};
